import type { DateRange } from '@cobliteam/common-types/src/dates';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import type { Configuration, IRequest } from '../../request/Types';
import type { MaintenanceCosts, MaintenanceReminderEditRequest, MaintenanceReportRequest, MaintenanceRow } from './Types';

export class NewMaintenanceRequester {
  private requester: Requester;

  private baseUrl: string;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
    this.baseUrl = 'analytics/v1/maintenance';
  }

  public getMaintenance = async (maintenanceId: string): Promise<MaintenanceRow> => {
    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/${maintenanceId}`,
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data, { deep: true }) as MaintenanceRow;
  };

  public getMaintenanceReport = async (request: MaintenanceReportRequest): Promise<Blob> => {
    const requestBody = {
      timezone: TimeZone.getTimezone(),
      ...snakecaseKeys(request),
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
      responseType: 'arraybuffer',
    };

    const response = await this.requester.request(requestData);
    return new Blob([response.data]);
  };

  public getMaintenanceCosts = (period: DateRange): Promise<MaintenanceCosts> => {
    const tz = TimeZone.getEncodedTimeZone();
    const startDate = new Date(period.start).toISOString();
    const endDate = new Date(period.end).toISOString();

    const requestData: IRequest = {
      endpoint: `/analytics/v1/maintenance/costs?start_date=${startDate}&end_date=${endDate}&timezone=${tz}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => camelcaseKeys(response.data, { deep: true }) as MaintenanceCosts);
  };

  public updateMaintenanceReminder = async (id: string, request: MaintenanceReminderEditRequest): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/reminder/${id}`,
      method: HttpMethod.PUT,
      body: JSON.stringify(snakecaseKeys(request)),
    };

    await this.requester.request(requestData);
  };

  public deleteMaintenanceReminder = async (id: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/reminder/${id}`,
      method: HttpMethod.DELETE,
    };

    await this.requester.request(requestData);
  };
}
